import { useState, useEffect, useRef } from "react";
import { motion, useInView } from "framer-motion";
import { Download, ArrowRight, CheckCircle } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const CaseStudiesSection = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, amount: 0.1 });
  const [caseStudies, setCaseStudies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [hasAnimated, setHasAnimated] = useState(false);

  useEffect(() => {
    if (isInView && !hasAnimated) {
      setHasAnimated(true);
    }
  }, [isInView, hasAnimated]);

  useEffect(() => {
    fetchCaseStudies();
  }, []);

  const fetchCaseStudies = async () => {
    try {
      const response = await axios.get(`${API}/case-studies`);
      setCaseStudies(response.data);
    } catch (error) {
      console.error("Error fetching case studies:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = async (caseStudy) => {
    try {
      const response = await axios.get(`${API}/case-studies/${caseStudy.id}/download`);
      
      if (response.data.download_url) {
        const link = document.createElement("a");
        link.href = response.data.download_url;
        link.download = response.data.filename || "LAB336_CaseStudy.pdf";
        link.target = "_blank";
        link.rel = "noopener noreferrer";
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        toast.success(`${response.data.filename} wird heruntergeladen...`);
      } else {
        toast.info(response.data.note);
      }
    } catch (error) {
      toast.error("Fehler beim Download. Bitte versuchen Sie es später erneut.");
    }
  };

  if (loading) {
    return (
      <section className="section-padding bg-lab-bg-light">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="animate-pulse space-y-8">
            <div className="h-8 bg-gray-200 w-1/4 rounded"></div>
            <div className="h-64 bg-gray-200 rounded"></div>
          </div>
        </div>
      </section>
    );
  }

  const featuredStudy = caseStudies[0];
  const otherStudies = caseStudies.slice(1);

  return (
    <section
      id="case-studies"
      className="section-padding bg-lab-bg-light"
      data-testid="case-studies-section"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        {/* Section Header */}
        <motion.div
          ref={ref}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="mb-16"
        >
          <span className="label-text">Fallstudien</span>
          <h2 className="font-playfair text-3xl lg:text-4xl font-semibold text-lab-primary mt-4 max-w-2xl">
            Erfolgsgeschichten unserer Kunden
          </h2>
        </motion.div>

        {/* Featured Case Study */}
        {featuredStudy && (
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="mb-12"
          >
            <div
              className="grid lg:grid-cols-2 gap-0 bg-white border border-lab-border overflow-hidden card-hover"
              data-testid={`case-study-${featuredStudy.id}`}
            >
              {/* Image */}
              <div className="relative aspect-[4/3] lg:aspect-auto">
                <img
                  src={featuredStudy.image_url}
                  alt={featuredStudy.title}
                  loading="lazy"
                  className="w-full h-full object-cover"
                />
                <div className="absolute top-4 left-4">
                  <span className="bg-lab-accent text-lab-primary px-4 py-2 text-sm font-semibold">
                    Featured
                  </span>
                </div>
              </div>

              {/* Content */}
              <div className="p-8 lg:p-12 flex flex-col">
                <div className="mb-2">
                  <span className="label-text">{featuredStudy.client}</span>
                </div>
                <h3 className="font-playfair text-2xl lg:text-3xl font-semibold text-lab-primary mb-4">
                  {featuredStudy.title}
                </h3>
                <p className="text-lab-text-muted leading-relaxed mb-6 flex-grow">
                  {featuredStudy.description}
                </p>

                {/* Results */}
                <div className="mb-8">
                  <p className="font-semibold text-lab-primary mb-3">
                    Ergebnisse:
                  </p>
                  <ul className="space-y-2">
                    {featuredStudy.results.map((result, i) => (
                      <li key={i} className="flex items-center gap-2">
                        <CheckCircle className="w-5 h-5 text-lab-accent flex-shrink-0" />
                        <span className="text-lab-text">{result}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                {/* Download Button */}
                <button
                  onClick={() => handleDownload(featuredStudy)}
                  className="btn-primary rounded-none flex items-center justify-center gap-2 group w-full lg:w-auto"
                  data-testid={`download-btn-${featuredStudy.id}`}
                >
                  <Download className="w-5 h-5" />
                  Fallstudie herunterladen (PDF)
                  <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform duration-200" />
                </button>
              </div>
            </div>
          </motion.div>
        )}

        {/* Other Case Studies */}
        {otherStudies.length > 0 && (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {otherStudies.map((study, index) => (
              <motion.div
                key={study.id}
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.3 + index * 0.1 }}
                className="bg-white border border-lab-border overflow-hidden card-hover group"
                data-testid={`case-study-${study.id}`}
              >
                <div className="relative aspect-[16/10] overflow-hidden">
                  <img
                    src={study.image_url}
                    alt={study.title}
                    loading="lazy"
                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                  />
                </div>
                <div className="p-6">
                  <span className="label-text text-xs">{study.client}</span>
                  <h3 className="font-playfair text-xl font-semibold text-lab-primary mt-2 mb-3">
                    {study.title}
                  </h3>
                  <p className="text-lab-text-muted text-sm mb-4 line-clamp-2">
                    {study.description}
                  </p>
                  <button
                    onClick={() => handleDownload(study)}
                    className="text-lab-accent font-medium text-sm flex items-center gap-1 hover:gap-2 transition-all duration-200"
                    data-testid={`download-btn-${study.id}`}
                  >
                    <Download className="w-4 h-4" />
                    PDF herunterladen
                  </button>
                </div>
              </motion.div>
            ))}
          </div>
        )}
      </div>
    </section>
  );
};

export default CaseStudiesSection;
